import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  Select,
  Title,
  Badge,
  Divider,
} from "@mantine/core";
import { IconCheck, IconX, IconClock } from "@tabler/icons-react";
import { useAuth } from "../../../context/AuthContext";
import { getApiEndpoint } from "../../../config/config";
import { useToast } from "../../../context/ToastContext";
import { handleGenericError } from "../../../utils/errorHandler";

function TaskDetailModal({
  isOpen,
  onClose,
  task,
  taskType, // 'created' or 'assigned'
  onTaskUpdated,
  users = [],
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Form state for created tasks (showing all fields)
  const [formData, setFormData] = useState({
    description: "",
    dueDate: "",
    assignedUser: "",
    response: "", // for assigned tasks
  });

  // Initialize form data when task or modal opens
  useEffect(() => {
    if (isOpen && task) {
      setFormData({
        description: task.task_details || "",
        dueDate: task.due_date ? task.due_date.split("T")[0] : "", // Format date for input
        assignedUser: task.assigned_user_season?.id?.toString() || "",
        response: "",
      });
    }
  }, [isOpen, task]);

  const handleClose = () => {
    setFormData({
      description: "",
      dueDate: "",
      assignedUser: "",
      response: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!task) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      let payload = {};
      let successMessage = "";

      if (taskType === "created") {
        // For created tasks, update the task details
        payload = {
          task_id: task.id,
          task_details: formData.description,
          due_date: formData.dueDate || null,
          assigned_user_season_id: formData.assignedUser || null,
        };
        successMessage = "Task updated successfully";
      } else {
        // For assigned tasks, mark as completed with optional response
        payload = {
          task_id: task.id,
          text_response: formData.response || null,
        };
        successMessage = "Task marked as completed";
      }

      const response = await fetch(getApiEndpoint("/v1/custom-task"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to update task: ${response.status}`
        );
      }

      showSuccess(successMessage);
      onTaskUpdated();
      handleClose();
    } catch (err) {
      handleGenericError(err, "Failed to update task. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!task) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      const response = await fetch(getApiEndpoint("/v1/custom-task/approve"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ task_id: task.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to approve task: ${response.status}`
        );
      }

      showSuccess("Task approved successfully!");
      onTaskUpdated();
      handleClose();
    } catch (err) {
      handleGenericError(err, "Failed to approve task. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!task) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication required");
      }

      const response = await fetch(getApiEndpoint("/v1/custom-task/reject"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ task_id: task.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to reject task: ${response.status}`
        );
      }

      showSuccess("Task rejected and reset for resubmission");
      onTaskUpdated();
      handleClose();
    } catch (err) {
      handleGenericError(err, "Failed to reject task. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!task) return null;

  const needsApproval =
    task.requires_admin_approval && task.completed_at && !task.admin_approved;
  const isApproved = task.admin_approved;
  const showApprovalButtons = taskType === "created" && needsApproval;

  const modalTitle =
    taskType === "created" ? "Edit Task" : "Mark Task as Completed";
  const submitButtonText =
    taskType === "created" ? "Update Task" : "Mark as Completed";

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={<Title order={4}>{modalTitle}</Title>}
      size="md"
      styles={{
        header: { backgroundColor: "#ffffff" },
        content: { backgroundColor: "#ffffff" },
      }}
    >
      <Stack gap="md">
        {/* Show approval status if applicable */}
        {task.requires_admin_approval && (
          <Group align="center" gap="xs">
            {needsApproval && (
              <Badge
                size="lg"
                color="yellow"
                leftSection={<IconClock size={14} />}
              >
                Pending Approval
              </Badge>
            )}
            {isApproved && (
              <Badge
                size="lg"
                color="green"
                leftSection={<IconCheck size={14} />}
              >
                Approved
              </Badge>
            )}
          </Group>
        )}

        {taskType === "created" ? (
          // Show all fields for created tasks (editable)
          <>
            {/* Show task response if completed and waiting for approval */}
            {task.completed_at && task.task_text_response && (
              <>
                <div>
                  <Text size="sm" fw={500} mb={4}>
                    User Response:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {task.task_text_response}
                  </Text>
                </div>
                <Divider />
              </>
            )}

            {showApprovalButtons ? (
              // Show approval buttons for completed tasks needing approval
              <Stack gap="md">
                <Text size="sm">
                  This task has been completed and is awaiting your approval.
                </Text>
                <Group grow>
                  <Button
                    color="green"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                    loading={submitting}
                  >
                    Approve
                  </Button>
                  <Button
                    color="red"
                    variant="outline"
                    leftSection={<IconX size={16} />}
                    onClick={handleReject}
                    loading={submitting}
                  >
                    Reject
                  </Button>
                </Group>
              </Stack>
            ) : (
              // Show edit fields for tasks not completed or already approved
              <>
                <Textarea
                  label="Task Description"
                  placeholder="Enter task description"
                  rows={3}
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                />

                <TextInput
                  label="Due Date"
                  placeholder="YYYY-MM-DD"
                  type="date"
                  value={formData.dueDate}
                  onChange={e =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                />

                <Select
                  label="Assigned User"
                  placeholder="Select user to assign"
                  data={users}
                  value={formData.assignedUser}
                  onChange={value =>
                    setFormData({ ...formData, assignedUser: value || "" })
                  }
                  searchable
                  clearable
                />
              </>
            )}
          </>
        ) : (
          // Show task details (read-only) and response field for assigned tasks
          <>
            <div>
              <Text size="md" mb={4}>
                {task.task_details}
              </Text>
            </div>

            <Textarea
              label="Response (Optional)"
              placeholder="Add any notes or comments about completing this task..."
              rows={3}
              value={formData.response}
              onChange={e =>
                setFormData({ ...formData, response: e.target.value })
              }
            />
          </>
        )}

        {!showApprovalButtons && (
          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={submitting}
              disabled={taskType === "created" && !formData.description.trim()}
            >
              {submitting ? "Saving..." : submitButtonText}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

export default TaskDetailModal;
